import Alpine from './alpine'

/**
 * Minimal rich-text editor over a contenteditable surface, mirroring the
 * formatting Shopify's description field offers.
 *
 * Deliberately dependency-free: a full editor bundle would be larger than the
 * rest of the admin's JavaScript combined, and the description field needs
 * bold, italic, headings, lists and links - nothing more.
 *
 * The hidden input is the source of truth for the form, so the surrounding
 * dirty-check and Save behave exactly as they do for a plain field.
 */
Alpine.data('richText', (initial = '') => ({
    html: initial,

    init() {
        this.$refs.surface.innerHTML = initial || ''
        this.sync()

        this.$refs.surface.addEventListener('input', () => this.sync())

        // Paste as plain text: pasting from Word or a web page otherwise drags
        // in a stylesheet's worth of inline styles.
        this.$refs.surface.addEventListener('paste', (event) => {
            event.preventDefault()
            const text = (event.clipboardData || window.clipboardData).getData('text/plain')
            document.execCommand('insertText', false, text)
        })
    },

    sync() {
        this.html = this.$refs.surface.innerHTML
        this.$refs.input.value = this.html
        this.$refs.input.dispatchEvent(new Event('input', { bubbles: true }))
    },

    run(command, value = null) {
        this.$refs.surface.focus()
        document.execCommand(command, false, value)
        this.sync()
    },

    isActive(command) {
        try {
            return document.queryCommandState(command)
        } catch {
            return false
        }
    },

    link() {
        const url = window.prompt('Link URL')
        if (url) this.run('createLink', url)
    },
}))

/**
 * Media picker: choose from the library or upload, reorder by drag, and keep a
 * hidden input list in the order shown so the server stores positions without
 * a second request.
 */
Alpine.data('mediaPicker', (initial = [], uploadUrl = '', csrf = '', libraryUrl = '') => ({
    items: initial,
    library: [],
    libraryOpen: false,
    libraryLoading: false,
    libraryQuery: '',
    libraryNext: null,
    uploading: 0,
    error: null,

    get ids() {
        return this.items.map((item) => item.id)
    },

    get featuredId() {
        return this.items.length ? this.items[0].id : ''
    },

    has(id) {
        return this.items.some((item) => item.id === id)
    },

    add(item) {
        if (!this.has(item.id)) this.items.push(item)
    },

    remove(id) {
        this.items = this.items.filter((item) => item.id !== id)
    },

    /*
     * Choosing something already uploaded.
     *
     * The same photo often belongs on a dozen products, and without this the only way
     * to put it on the twelfth is to upload it again - which is how a file library ends
     * up with twelve copies of one image and no way to tell which is in use.
     */
    async openLibrary() {
        this.libraryOpen = true

        if (this.library.length === 0) await this.loadLibrary()
    },

    async searchLibrary() {
        this.library = []
        this.libraryNext = null
        await this.loadLibrary()
    },

    async loadLibrary(url = null) {
        if (!libraryUrl || this.libraryLoading) return

        this.libraryLoading = true

        const target = url || `${libraryUrl}?kind=image&q=${encodeURIComponent(this.libraryQuery)}`

        try {
            const response = await fetch(target, { headers: { Accept: 'application/json' } })

            if (!response.ok) throw new Error(`Could not load the library (${response.status})`)

            const payload = await response.json()

            this.library = [...this.library, ...(payload.data || [])]
            this.libraryNext = payload.next_page_url || null
        } catch (error) {
            this.error = error.message
            Alpine.store('toast').error(error.message)
        } finally {
            this.libraryLoading = false
        }
    },

    /** Tick and untick, because choosing the wrong one is the common mistake. */
    toggle(item) {
        this.has(item.id) ? this.remove(item.id) : this.add(item)
    },

    /** Promote to first position, which is what "featured image" means here. */
    makeFeatured(id) {
        const index = this.items.findIndex((item) => item.id === id)
        if (index > 0) this.items.unshift(this.items.splice(index, 1)[0])
    },

    move(id, direction) {
        const index = this.items.findIndex((item) => item.id === id)
        const target = index + direction
        if (index < 0 || target < 0 || target >= this.items.length) return
        const [item] = this.items.splice(index, 1)
        this.items.splice(target, 0, item)
    },

    async upload(event) {
        const files = [...event.target.files]
        event.target.value = ''
        if (!files.length) return

        this.error = null
        this.uploading += files.length

        for (const file of files) {
            const body = new FormData()
            body.append('file', file)

            try {
                const response = await fetch(uploadUrl, {
                    method: 'POST',
                    body,
                    headers: { 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
                })

                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}))
                    throw new Error(payload.message || `Upload failed (${response.status})`)
                }

                this.add(await response.json())
            } catch (error) {
                this.error = error.message
                Alpine.store('toast').error(error.message)
            } finally {
                this.uploading--
            }
        }
    },
}))

/**
 * The option/variant editor.
 *
 * Builds the combination list in the browser so the merchant sees the grid the
 * server will generate before saving, rather than discovering it afterwards.
 * The same ordering rule as the server: options in order, values in order.
 */
Alpine.data('variantEditor', (initialOptions = [], initialVariants = {}) => ({
    options: initialOptions.length ? initialOptions : [],
    variants: initialVariants,

    addOption() {
        if (this.options.length >= 3) return
        this.options.push({ name: '', values: '' })
    },

    removeOption(index) {
        this.options.splice(index, 1)
    },

    valuesOf(option) {
        return (option.values || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean)
            .filter((value, index, all) =>
                all.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index
            )
    },

    get usableOptions() {
        return this.options.filter((option) => option.name.trim() && this.valuesOf(option).length)
    },

    get hasOptions() {
        return this.usableOptions.length > 0
    },

    /** Cartesian product, in option order - matches VariantMatrix on the server. */
    get combinations() {
        const lists = this.usableOptions.map((option) => this.valuesOf(option))
        if (!lists.length) return []

        return lists.reduce(
            (rows, values) => rows.flatMap((row) => values.map((value) => [...row, value])),
            [[]]
        )
    },

    get rows() {
        return this.combinations.map((combination) => combination.join(' / '))
    },

    field(key, name, fallback = '') {
        if (!this.variants[key]) this.variants[key] = {}
        if (this.variants[key][name] === undefined) this.variants[key][name] = fallback
        return this.variants[key][name]
    },

    /** Copy the first row's value down the column - the usual bulk edit. */
    applyToAll(name) {
        const rows = this.rows
        if (rows.length < 2) return
        const value = this.variants[rows[0]]?.[name] ?? ''
        rows.slice(1).forEach((row) => {
            if (!this.variants[row]) this.variants[row] = {}
            this.variants[row][name] = value
        })
    },
}))

/** Live profit and margin beside the price fields. */
Alpine.data('pricingCalculator', (price = '', cost = '') => ({
    price,
    cost,

    get profit() {
        const p = parseFloat(this.price)
        const c = parseFloat(this.cost)
        if (isNaN(p) || isNaN(c)) return null
        return (p - c).toFixed(2)
    },

    get margin() {
        const p = parseFloat(this.price)
        const c = parseFloat(this.cost)
        if (isNaN(p) || isNaN(c) || p === 0) return null
        return (((p - c) / p) * 100).toFixed(1)
    },
}))

/** Tag input: chips backed by one comma-separated hidden field. */
Alpine.data('tagInput', (initial = '') => ({
    tags: initial ? initial.split(',').map((tag) => tag.trim()).filter(Boolean) : [],
    draft: '',

    get value() {
        return this.tags.join(', ')
    },

    add() {
        const tag = this.draft.trim().replace(/,$/, '')
        this.draft = ''
        if (!tag) return
        if (this.tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return
        this.tags.push(tag)
        this.emit()
    },

    remove(tag) {
        this.tags = this.tags.filter((existing) => existing !== tag)
        this.emit()
    },

    /** Backspace on an empty box removes the last chip. */
    backspace() {
        if (this.draft === '' && this.tags.length) {
            this.tags.pop()
            this.emit()
        }
    },

    emit() {
        this.$nextTick(() => this.$refs.input?.dispatchEvent(new Event('input', { bubbles: true })))
    },
}))
